import React, { ReactNode } from "react";
import UIService from "../../../services/UIService";
import OwTitle from "../OwTitle/OwTitle";
import { OwFnTable } from "../OwFnTable/OwFnTable";
import PaginationBar from "../PaginationBar/PaginationBar";
import IOwTableCols from "../../../models/IOwTableCols";
import IOwTableConfig from "../../../models/IOwTableConfig";
import "./OwTableCard.scss";

type Props = {
    /**
     * Card header title (translation key, unless disableTitleTranslation is set).
     */
    title: string;
    /**
     * Optional count rendered next to the title, e.g. "Open transfers (3)".
     */
    count?: number;
    /**
     * Left icon class for the header title.
     */
    leftIconName?: string;
    /**
     * Skip translation of the title.
     */
    disableTitleTranslation?: boolean;
    /**
     * Right-aligned slot in the header for actions (buttons, etc.).
     */
    actions?: ReactNode;
    /**
     * Optional content rendered between the header and the table (filters, search, etc.).
     */
    children?: ReactNode;

    // --- Table pass-through (see OwFnTable) ---
    columns: IOwTableCols[];
    dataSet: any[];
    dataFetch: boolean;
    initialData: boolean;
    tableConfig?: IOwTableConfig;
    /**
     * Escape hatch for any other OwFnTable prop (detail view, sticky header, sort, etc.).
     */
    tableProps?: Partial<React.ComponentProps<typeof OwFnTable>>;

    // --- Pagination (footer only rendered when onPaginationChanged is provided and there are rows) ---
    offset?: number;
    limit?: number;
    totalRecords?: number;
    onPaginationChanged?: (offset: number, limit: number) => void;
};

export const OwTableCard = (props: Props) => {
    const themeClass = UIService.isMainViewLight() ? "OwTableCard--light" : "OwTableCard--dark";

    const showPagination = !!props.onPaginationChanged && props.dataSet.length > 0;

    return (
        <div className={"OwTableCard card w-100 " + themeClass}>
            <div className="OwTableCard__header">
                <OwTitle
                    title={props.title}
                    tag={"h5"}
                    leftIconName={props.leftIconName}
                    disableTranslation={props.disableTitleTranslation}
                    defaultMargins={false}
                    className={"OwTableCard__title mb-0"}
                />
                {typeof props.count === "number" ? <span className="OwTableCard__count ms-2">({props.count})</span> : null}

                {props.actions ? <div className="OwTableCard__actions ms-auto">{props.actions}</div> : null}
            </div>

            {props.children ? <div className="OwTableCard__filters">{props.children}</div> : null}

            <div className="OwTableCard__body">
                <OwFnTable
                    columns={props.columns}
                    dataSet={props.dataSet}
                    dataFetch={props.dataFetch}
                    initialData={props.initialData}
                    tableConfig={props.tableConfig}
                    {...props.tableProps}
                />
            </div>

            {showPagination ? (
                <div className="OwTableCard__footer">
                    <PaginationBar
                        offset={props.offset ?? 0}
                        limit={props.limit ?? 100}
                        totalRecords={props.totalRecords ?? 0}
                        onPaginationChanged={props.onPaginationChanged!}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default OwTableCard;
